import React, { useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { get, find } from "lodash";
import { apiBase } from "../../utils/apiBase";
import { stringifyFilters } from "../../utils/queryParser";
import qs from "query-string";
import { useEntities } from "../../queries/entities";

import { Select, Spin } from "antd";

import styles from "./SearchSelect.module.scss";

async function fetchData({
  apiPath,
  searchField,
  query,
  renderLabel = (row) => row.name,
  renderExtra = true,
  entities,
  extraParams = {},
  baseFilters = {},
}) {
  const queryString = qs.stringify({
    limit: query.limit ? parseInt(query.limit, 10) : 25,
    page: query.page ? parseInt(query.page, 10) : 1,
    filters: stringifyFilters({ ...baseFilters, [searchField]: query }),
    ...extraParams,
  });

  return apiBase
    .get(`${apiPath}?${queryString}`)
    .then((res) => {
      const dataKey = apiPath.replace(/-/g, "_");
      return get(res, `data.${dataKey}`, []).map((row) => {
        const entity = find(entities, { id: row.entity_id });
        return {
          value: row.id,
          row,
          label: (
            <div className={styles.optionSimple}>
              <div className={styles.main}>{renderLabel(row)}</div>
              {renderExtra && (
                <div className={styles.extra}>
                  {!!entity ? `[${row.id}, ${entity.name}]` : `${row.id}`}
                </div>
              )}
            </div>
          ),
        };
      });
    })
    .catch((err) => {
      console.log(err);
    });
}

function SearchSelect({
  debounceTimeout = 400,
  fetchOptions = fetchData,
  apiPath = "assets",
  searchField = "name",
  renderLabel = (row) => row.name,
  renderExtra = true,
  defaultOptions = [],
  labels = {},
  extraParams = {},
  baseFilters = {},
  ...props
}) {
  const { data: entities = [] } = useEntities();

  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState(defaultOptions);
  const fetchRef = useRef(0);
  const extraParamsKey = JSON.stringify(extraParams);
  const baseFiltersKey = JSON.stringify(baseFilters);

  const debounceFetcher = useMemo(() => {
    const loadOptions = (query) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);
      setFetching(true);
      fetchOptions({
        apiPath,
        searchField,
        query,
        entities,
        renderLabel,
        renderExtra,
        extraParams,
        baseFilters,
      }).then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          // for fetch callback order
          return;
        }
        setOptions(newOptions);
        setFetching(false);
      });
    };
    return debounce(loadOptions, debounceTimeout);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchOptions,
    debounceTimeout,
    apiPath,
    searchField,
    entities,
    extraParamsKey,
    baseFiltersKey,
  ]);

  const value = props.value;
  // A stored value is only an id -- without an option carrying its label the
  // Select renders the raw id, and searching drops the fetched options that
  // would have covered it. Merge the caller's known labels back in every time.
  const mergedOptions = useMemo(() => {
    const selected = (Array.isArray(value) ? value : [value]).filter(
      (v) => v !== undefined && v !== null
    );
    const fallbacks = selected
      .filter((v) => !options.some((option) => option.value === v))
      .flatMap((v) => {
        const label =
          labels[v] ??
          defaultOptions.find((option) => option.value === v)?.label;
        return label === undefined ? [] : [{ value: v, label }];
      });
    return fallbacks.length ? [...fallbacks, ...options] : options;
  }, [value, labels, options, defaultOptions]);

  return (
    <Select
      placeholder="Select items"
      className="App__fullWidth"
      // dropdownStyle={{ height: "600px" }}
      listHeight={400}
      allowClear
      showSearch
      filterOption={false}
      onSearch={debounceFetcher}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      {...props}
      options={mergedOptions}
    />
  );
}

export default SearchSelect;
